import React from 'react';
import Color from 'color';
import type { JSONSchema4 } from 'json-schema';
import {
  Box,
  Typography,
  InputLabel,
  Grid,
  TextField,
  styled,
} from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import PercentIcon from '@mui/icons-material/Percent';
import { FieldControlLabel } from '../layout/FieldControlLabel';
import type { GroupedPercentileListValue } from './GroupedPercentileList/types';

type GroupedPercentileListProps = {
  schemaProps: JSONSchema4 | JSONSchema4[] | undefined;
  error?: string;
  onChange: (value: GroupedPercentileListValue) => void;
  value: GroupedPercentileListValue;
  disabled?: boolean;
  fieldId: string;
  formId: string;
};

export const GroupedPercentileList = (props: GroupedPercentileListProps) => {
  const fillEmptyValues = (values: GroupedPercentileListValue['values']) => {
    const newValues = [...values];
    // intentionally using for loop to deal with sparse arrays
    for (let i = 0; i < values.length; i++) {
      if (!newValues[i]?.props) {
        newValues[i] = {
          props: {},
        };
      }
    }
    return newValues;
  };

  return (
    <Box>
      {props?.schemaProps?.map((section: any, index: number) => (
        <Box key={section.title} mb={4}>
          <FieldControlLabel
            hasLabel
            isRequired={false}
            text={section.title}
            fieldId={props.fieldId}
            formId={props.formId}
          />
          <ListContainer>
            {Object.keys(section.properties.props.properties).map(
              (fieldKey: string) => {
                const fieldSchema =
                  section.properties.props.properties[fieldKey];
                const handleItemPercentFieldChange: TextFieldProps['onChange'] =
                  (e) => {
                    const updatedValues = fillEmptyValues(props.value.values);

                    updatedValues[index] = {
                      ...updatedValues[index],
                      props: {
                        ...updatedValues[index]?.props,
                        [fieldKey]: {
                          ...updatedValues[index]?.props?.[fieldKey],
                          value: parseFloat(e.target.value),
                        },
                      },
                    };

                    const updatedListValue: GroupedPercentileListValue = {
                      ...props.value,
                      values: updatedValues,
                    };

                    props.onChange(updatedListValue);
                  };

                const handleCommentChange: TextFieldProps['onChange'] = (e) => {
                  const updatedValues = [...props.value.values];
                  updatedValues[index] = {
                    ...updatedValues[index],
                    props: {
                      ...updatedValues[index]?.props,
                      [fieldKey]: {
                        ...updatedValues[index]?.props?.[fieldKey],
                        comment: e.target.value,
                      },
                    },
                  };

                  const updatedListValue: GroupedPercentileListValue = {
                    ...props.value,
                    values: updatedValues,
                  };

                  props.onChange(updatedListValue);
                };

                return (
                  <StyledRow item sm={12} key={fieldKey}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        width: '100%',
                      }}
                    >
                      <InputLabel>{fieldSchema.label}</InputLabel>
                      {fieldSchema.commentEligibility === 'REQUIRED' && (
                        <StyledTextField
                          variant="standard"
                          sx={{
                            width: 'auto',
                            '& .MuiInputBase-input': {
                              width: '8rem',
                            },
                          }}
                          disabled={props.disabled}
                          onChange={handleCommentChange}
                          value={
                            props.value?.values?.[index]?.props?.[fieldKey]
                              ?.comment ?? ''
                          }
                          placeholder="Description"
                        />
                      )}
                    </Box>
                    <StyledTextField
                      variant="standard"
                      InputProps={{
                        endAdornment: <PercentIcon />,
                        inputProps: { min: 0 },
                      }}
                      type="number"
                      sx={{
                        '& .MuiInputBase-input': {
                          width: '4rem',
                        },
                      }}
                      disabled={props.disabled}
                      fullWidth
                      onChange={handleItemPercentFieldChange}
                      value={
                        props.value?.values?.[index]?.props?.[fieldKey]
                          ?.value || 0
                      }
                    />
                  </StyledRow>
                );
              }
            )}

            <Total item sm={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  paddingRight: '8px',
                }}
              >
                <InputLabel>Total:</InputLabel>
                <StyledTextField
                  variant="standard"
                  InputProps={{
                    endAdornment: <PercentIcon />,
                    inputProps: { min: 0 },
                  }}
                  type="number"
                  disabled
                  fullWidth={false}
                  value={Object.keys(
                    section.properties.props.properties
                  ).reduce(
                    (sum: number, fieldKey: string) =>
                      sum +
                      (parseFloat(
                        props.value?.values?.[index]?.props?.[
                          fieldKey
                        ]?.value?.toString()
                      ) || 0),
                    0
                  )}
                />
              </Box>
            </Total>
          </ListContainer>
        </Box>
      ))}
      {props.error && (
        <Typography color="error" variant="body2">
          {props.error}
        </Typography>
      )}
    </Box>
  );
};

const ListContainer = styled(Grid)`
  border-radius: 6px;
  border: 1px solid rgb(0, 0, 0, 0.1);
  margin-top: 2px;
`;

const RowBase = styled(Grid)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  padding: 8px;
`;

const StyledRow = styled(RowBase)`
  &:nth-child(odd) {
    background: ${(props) =>
      props.theme?.palette.mode === 'light'
        ? props.theme?.palette.background.modal
        : Color(props.theme?.palette.background.modal).darken(0.15).toString()};
  }
`;

const StyledTextField = styled(TextField)(
  ({ theme }) => `
  & .MuiInputBase-input {
    padding-left: 2px;
    background: transparent;
    border: none;
    border-bottom: 1px solid ${theme.palette.text.contrastText};
    border-radius: 0;

    height: 0.15rem;
    width: 6rem;
    text-align: right;

    &.Mui-disabled {
      background: transparent;
    }
  }

  & .MuiInputBase-root {
    justify-content: flex-end;
  }
`
);

const Total = styled(Grid)`
  display: flex;
  justify-content: flex-end;
  padding: 8px;
  width: 100%;
`;
